import React, { useState, useEffect, useImperativeHandle } from 'react';
import { Calendar, DollarSign, FileDown, Trash2 } from 'lucide-react';
import { generateDG30PDF, generateDG60PDF, fmt } from '../../../../utils/workflowPDF';
import { useDGSession } from '../../../../hooks/useDGSession';
import EquipeDaEtapa, { equipeNoWorkflow } from '../../shared/EquipeDaEtapa';
import ImportarDaEtapa from '../../shared/ImportarDaEtapa';
import { moverLinha } from '../../../../utils/moverLinha';
import { useArrastarLinhas, AlcaDeArrastar } from '../../shared/useArrastarLinhas';
import { transferenciasSemDiagnostico } from '../../../../utils/importarDaEtapa';
import { dgTemConteudo } from '../../../../utils/dgMapeamento';
import { fetchTeSessionRef, fetchApprovedTransfers, teAgora } from '../../../../data/te';
import { getPending } from '../../../../data/local/pending';
import { dgSincronizadaComATE, algoMudou } from '../../../../utils/dgDaTE';
import { BILLING_MODELS, MARK } from '../../../../constants';
import {
  EMPTY_PREGNANCY_DATA,
  previsaoDeParto,
  DIAGNOSTIC_METHODS,
  DG30_RESULTS,
  EMPTY_CAUSES,
  INCONCLUSIVE_REASONS,
  DG60_RESULTS,
  HEARTBEAT_STATUS,
  FETUS_SEX,
  SEXING_CONFIDENCE
} from '../../../../constants/workflowGranular';

/**
 * Aba Prenhez - DG 30D e 60D
 * Layout tabular para desktop, receptoras pre-carregadas da TE
 */
const PregnancyTab = React.forwardRef(({ service, workflowData, onUpdate, laboratoryId, isActive = true }, ref) => {
  const { loadDGData, saveDGData } = useDGSession(laboratoryId);
  const [pregnancyData, setPregnancyData] = useState(EMPTY_PREGNANCY_DATA);
  const [isInitialized, setIsInitialized] = useState(false);
  const equipe30 = equipeNoWorkflow(workflowData, onUpdate, 'dg30');
  const equipe60 = equipeNoWorkflow(workflowData, onUpdate, 'dg60');
  const [activeSubTab, setActiveSubTab] = useState('dg30');
  const [avisoDaTE, setAvisoDaTE] = useState(null);

  const [manualQuantity30D, setManualQuantity30D] = useState(service?.pregnancy_30d_quantity || 0);
  const [manualQuantity60D, setManualQuantity60D] = useState(service?.pregnancy_60d_quantity || 0);

  useEffect(() => {
    if (isInitialized || !laboratoryId || !service?.id) return;

    const initializeData = async () => {
      // Try normalized tables first (só usa se tem checks salvos)
      const normalized = await loadDGData(service?.id);
      const normalizedHasChecks = normalized &&
        (normalized.dg30?.checks?.length > 0 || normalized.dg60?.checks?.length > 0);
      if (normalizedHasChecks) {
        setPregnancyData({ ...EMPTY_PREGNANCY_DATA, ...normalized });
        setIsInitialized(true);
        return;
      }

      if (service?.pregnancy_data?.dg30?.checks?.length > 0) {
        setPregnancyData(service.pregnancy_data);
      } else {
        // Busca transferências aprovadas nas tabelas normalizadas
        let dg30Checks = [];
        let teDate = null;

        const teSession = await fetchTeSessionRef(service.id);

        if (teSession?.id) {
          teDate = teSession.session_date;
          const transfers = await fetchApprovedTransfers(teSession.id);

          dg30Checks = (transfers || []).map((t, index) => ({
            id: `dg30-${index}`,
            transfer_id: t.id,
            recipient_id: t.recipient_id,
            recipient_identifier: t.recipient_identifier,
            embryo_number: t.embryo_number,
            te_date: teDate,
            expected_date: t.expected_dg30,
            result: null,
            viability: null,
            heartbeat: null,
            size_mm: null,
            empty_cause: null,
            inconclusive_reason: null,
            reschedule_date: null,
            observations: ''
          }));
        }

        // Fallback legado (serviços antigos com te_data JSONB)
        if (dg30Checks.length === 0 && service?.te_data?.transfers) {
          const teTransfers = service.te_data.transfers || [];
          dg30Checks = teTransfers
            .filter(t => t.status === 'APROVADA')
            .map((t, index) => ({
              id: `dg30-${index}`,
              transfer_id: t.id,
              recipient_id: t.recipient?.animal_id,
              recipient_identifier: t.recipient?.identifier,
              embryo_number: t.embryo_number,
              te_date: service?.te_data?.session?.date,
              expected_date: t.dates?.expected_dg30,
              result: null, viability: null, heartbeat: null, size_mm: null,
              empty_cause: null, inconclusive_reason: null,
              reschedule_date: null, observations: ''
            }));
        }

        if (dg30Checks.length > 0) {
          setPregnancyData({
            dg30: {
              session: { date: workflowData?.pregnancy_30d_date || service?.pregnancy_30d_scheduled_date || null, technician_id: null, technician_name: '', method: 'Ultrassom' },
              checks: dg30Checks,
              completed: workflowData?.pregnancy_30d_done || false
            },
            dg60: {
              session: { date: workflowData?.pregnancy_60d_date || service?.pregnancy_60d_scheduled_date || null, technician_id: null, technician_name: '', method: 'Ultrassom' },
              checks: [],
              completed: workflowData?.pregnancy_60d_done || false,
              billing_quantity: workflowData?.pregnancy_60d_quantity || 0
            }
          });
        } else {
          setPregnancyData(EMPTY_PREGNANCY_DATA);
        }
      }
      setIsInitialized(true);
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id, laboratoryId, isInitialized]);

  const handleDG30SessionChange = (field, value) => {
    setPregnancyData(prev => ({
      ...prev,
      dg30: { ...prev.dg30, session: { ...prev.dg30.session, [field]: value } }
    }));
  };

  const handleDG30CheckChange = (index, field, value) => {
    setPregnancyData(prev => ({
      ...prev,
      dg30: {
        ...prev.dg30,
        checks: prev.dg30.checks.map((check, i) => {
          if (i !== index) return check;
          const updated = { ...check, [field]: value };
          if (field === 'result' && value === DG30_RESULTS.EMPTY) {
            updated.viability = null; updated.heartbeat = null; updated.size_mm = null;
            updated.inconclusive_reason = null; updated.reschedule_date = null;
          }
          if (field === 'result' && value === DG30_RESULTS.INCONCLUSIVE) {
            updated.viability = null; updated.heartbeat = null; updated.size_mm = null;
            updated.empty_cause = null;
          }
          if (field === 'result' && value === DG30_RESULTS.PREGNANT) {
            updated.empty_cause = null; updated.inconclusive_reason = null; updated.reschedule_date = null;
          }
          return updated;
        })
      }
    }));
  };

  const handleDG60SessionChange = (field, value) => {
    setPregnancyData(prev => ({
      ...prev,
      dg60: { ...prev.dg60, session: { ...prev.dg60.session, [field]: value } }
    }));
  };

  const handleImportFromDG30 = () => {
    const dg60Checks = pregnancyData.dg30.checks.map((check, index) => ({
      id: `dg60-${index}`,
      transfer_id: check.transfer_id,
      recipient_id: check.recipient_id,
      recipient_identifier: check.recipient_identifier,
      embryo_number: check.embryo_number,
      te_date: check.te_date,
      dg30_date: pregnancyData.dg30.session.date,
      expected_date: null,
      result: null,
      viability: null,
      heartbeat: null,
      size_mm: null,
      placentomes_count: null,
      sexing: { performed: false, sex: null, confidence: null },
      abort_data: null,
      observations: ''
    }));
    setPregnancyData(prev => ({ ...prev, dg60: { ...prev.dg60, checks: dg60Checks } }));
    alert(`${dg60Checks.length} receptoras importadas do DG 30D!`);
  };

  const handleDG60CheckChange = (index, field, value) => {
    setPregnancyData(prev => ({
      ...prev,
      dg60: {
        ...prev.dg60,
        checks: prev.dg60.checks.map((check, i) => {
          if (i !== index) return check;
          const updated = { ...check };
          if (field.includes('.')) {
            const [parent, child] = field.split('.');
            updated[parent] = { ...updated[parent], [child]: value };
          } else {
            updated[field] = value;
          }
          if (field === 'result' && value === DG60_RESULTS.ABORTED) {
            updated.viability = null; updated.heartbeat = null; updated.size_mm = null;
            updated.placentomes_count = null;
            updated.sexing = { performed: false, sex: null, confidence: null };
            if (!updated.abort_data) {
              updated.abort_data = { date: pregnancyData.dg60.session.date, stage: 'early', cause: null };
            }
          }
          if (field === 'result' && value === DG60_RESULTS.PREGNANT) {
            updated.abort_data = null;
          }
          return updated;
        })
      }
    }));
  };

  const calculateBillingQuantity = () => {
    return pregnancyData.dg60.checks.filter(check => check.result === DG60_RESULTS.PREGNANT).length;
  };

  useEffect(() => {
    if (!isInitialized) return;

    const billingQty = calculateBillingQuantity();

    onUpdate('pregnancy_30d_date', pregnancyData.dg30.session.date);
    onUpdate('pregnancy_30d_done', pregnancyData.dg30.completed);
    const pregnant30D = pregnancyData.dg30.checks.filter(c => c.result === DG30_RESULTS.PREGNANT).length;
    const finalQuantity30D = pregnancyData.dg30.checks.length === 0 ? manualQuantity30D : pregnant30D;
    onUpdate('pregnancy_30d_quantity', finalQuantity30D);

    onUpdate('pregnancy_60d_date', pregnancyData.dg60.session.date);
    onUpdate('pregnancy_60d_done', pregnancyData.dg60.completed);
    const finalQuantity60D = pregnancyData.dg60.checks.length === 0 ? manualQuantity60D : billingQty;
    onUpdate('pregnancy_60d_quantity', finalQuantity60D);

    if (pregnancyData.dg60.checks.length === 0 && pregnancyData.dg30.checks.length > 0) {
      const dg60Checks = pregnancyData.dg30.checks.map((check, index) => ({
        id: `dg60-${index}`,
        transfer_id: check.transfer_id,
        recipient_id: check.recipient_id,
        recipient_identifier: check.recipient_identifier,
        embryo_number: check.embryo_number,
        te_date: check.te_date,
        dg30_date: pregnancyData.dg30.session.date,
        dg30_result: check.result,
        expected_date: null,
        result: null,
        viability: null,
        heartbeat: null,
        size_mm: null,
        placentomes_count: null,
        sexing: { performed: false, sex: null, confidence: null },
        abort_data: null,
        observations: ''
      }));
      setPregnancyData(prev => ({ ...prev, dg60: { ...prev.dg60, checks: dg60Checks } }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pregnancyData, isInitialized, manualQuantity30D, manualQuantity60D]);

  /**
   * A DG acertada com a TE de agora (a regra está em `dgSincronizadaComATE`).
   *
   * Devolve os dados novos, ou null quando não há o que acertar. Não mexe
   * quando a TE não tem transferência aprovada — serviço antigo, de
   * quantidade digitada à mão, ficaria sem DG — nem quando há TE lançada
   * offline esperando subir: o banco ainda não é a TE de verdade.
   */
  const acertarComATE = async (dados) => {
    if (await getPending('te', service.id)) return null;
    const te = await teAgora(service.id);
    if (!te || te.transfers.length === 0) return null;

    const dg30 = dgSincronizadaComATE(dados.dg30?.checks, te.transfers,
      { teDate: te.teDate, previsto: 'expected_dg30', prefixo: 'dg30' });
    const dg60 = (dados.dg60?.checks || []).length > 0
      ? dgSincronizadaComATE(dados.dg60.checks, te.transfers, { teDate: te.teDate, prefixo: 'dg60' })
      : null;
    if (!algoMudou(dg30.mudou) && !algoMudou(dg60?.mudou)
      && !dg30.mudou.foraDaTE && !dg60?.mudou.foraDaTE) return null;

    return {
      dados: {
        ...dados,
        dg30: { ...dados.dg30, checks: dg30.checks },
        ...(dg60 ? { dg60: { ...dados.dg60, checks: dg60.checks } } : {}),
      },
      mudou: dg30.mudou,
    };
  };

  /**
   * Abrir a aba acerta a DG com a TE, e diz o que mudou.
   *
   * As abas são montadas juntas, escondidas por CSS: a DG lia a TE uma vez,
   * na abertura do serviço. Trocar a receptora na TE e passar para cá
   * mostrava a antiga.
   */
  useEffect(() => {
    if (!isActive || !isInitialized || !service?.id) return;
    let vivo = true;
    acertarComATE(pregnancyData).then(r => {
      if (!vivo || !r) return;
      setPregnancyData(r.dados);
      const { atualizadas, entraram, sairam } = r.mudou;
      const partes = [
        atualizadas && `${atualizadas} atualizada(s)`,
        entraram && `${entraram} nova(s)`,
        sairam && `${sairam} que saíram da TE removida(s)`,
      ].filter(Boolean);
      setAvisoDaTE(partes.length ? `Receptoras acertadas com a TE: ${partes.join(', ')}.` : null);
    }).catch(err => console.warn('[dg] não deu para conferir com a TE:', err.message));
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isInitialized, service?.id]);

  /** Tira a linha que não está mais na TE (só ela tem lixeira). */
  const removerForaDaTE = (chave, index) => {
    const check = pregnancyData[chave].checks[index];
    const pergunta = `Tirar a receptora ${check.recipient_identifier || ''} do diagnóstico?\n`
      + 'Ela não está mais entre as transferências da TE. O resultado lançado nesta linha vai junto.';
    if (!window.confirm(pergunta)) return;
    setPregnancyData(prev => ({
      ...prev,
      [chave]: { ...prev[chave], checks: prev[chave].checks.filter((_, i) => i !== index) },
    }));
  };

  /**
   * Traz da TE as receptoras que ainda não têm linha de diagnóstico.
   *
   * O DG lê as transferências aprovadas uma vez, na primeira abertura, e só
   * quando ainda não há nenhum check gravado. Transferência aprovada depois
   * disso — ou aprovada enquanto o DG já tinha duas linhas — nunca aparecia.
   *
   * Casamento pelo `transfer_id`, que é o que liga as duas etapas. O resultado
   * já digitado não é tocado: só entram as que faltam.
   */
  const importarDaTE = async () => {
    const teSession = await fetchTeSessionRef(service.id);
    if (!teSession?.id) return 0;

    const transfers = await fetchApprovedTransfers(teSession.id) || [];
    const faltando = transferenciasSemDiagnostico(pregnancyData.dg30?.checks, transfers);
    if (faltando.length === 0) return 0;

    // O id é só chave de React. `conf` e o relógio evitam colidir com os
    // `dg30-N` que já estão na lista.
    const marca = Date.now();
    const novos = faltando.map((t, i) => ({
      id: `dg30-conf-${marca}-${i}`,
      transfer_id: t.id,
      recipient_id: t.recipient_id,
      recipient_identifier: t.recipient_identifier,
      embryo_number: t.embryo_number,
      te_date: teSession.session_date,
      expected_date: t.expected_dg30,
      result: null,
      viability: null,
      heartbeat: null,
      size_mm: null,
      empty_cause: null,
      inconclusive_reason: null,
      reschedule_date: null,
      observations: ''
    }));

    setPregnancyData(prev => ({
      ...prev,
      dg30: { ...prev.dg30, checks: [...(prev.dg30?.checks || []), ...novos] },
    }));
    return novos.length;
  };

  /**
   * "Substituir pela TE": o DG30 refeito com as transferências aprovadas de
   * agora, uma linha por transferência, na ordem da TE. Os resultados já
   * digitados vão embora — é o que se pediu. Só na tela até o "Salvar".
   *
   * O DG60 não muda junto: ele tem o próprio "importar do DG30", e trocar os
   * dois num clique só apagaria um diagnóstico que a pessoa nem estava olhando.
   */
  const substituirPelaTE = async () => {
    const teSession = await fetchTeSessionRef(service.id);
    const transfers = teSession?.id ? (await fetchApprovedTransfers(teSession.id) || []) : [];
    if (transfers.length === 0) return { bloqueio: 'A TE não tem transferências aprovadas.' };

    const atuais = pregnancyData.dg30?.checks || [];
    const comResultado = atuais.filter(c => c.result).length;
    const pergunta = [
      `Substituir as ${atuais.length} linhas do DG30 pelas ${transfers.length} transferências aprovadas da TE?`,
      '',
      comResultado > 0
        ? `Vai junto: ${comResultado} resultado(s) de diagnóstico já lançado(s).`
        : 'Nenhum resultado lançado será perdido.',
      '',
      'O DG60 não muda. Nada é gravado até "Salvar Workflow".',
    ].join('\n');
    if (atuais.length > 0 && !window.confirm(pergunta)) return null;

    const marca = Date.now();
    const novos = transfers.map((t, i) => ({
      id: `dg30-subst-${marca}-${i}`,
      transfer_id: t.id,
      recipient_id: t.recipient_id,
      recipient_identifier: t.recipient_identifier,
      embryo_number: t.embryo_number,
      te_date: teSession.session_date,
      expected_date: t.expected_dg30,
      result: null,
      viability: null,
      heartbeat: null,
      size_mm: null,
      empty_cause: null,
      inconclusive_reason: null,
      reschedule_date: null,
      observations: ''
    }));
    setPregnancyData(prev => ({ ...prev, dg30: { ...prev.dg30, checks: novos } }));
    return novos.length;
  };

  // A ordem das receptoras em cada DG é o sort_order que o salvamento grava.
  const arrastarDG30 = useArrastarLinhas((de, para) => setPregnancyData(prev => ({
    ...prev, dg30: { ...prev.dg30, checks: moverLinha(prev.dg30.checks, de, para) },
  })));
  const arrastarDG60 = useArrastarLinhas((de, para) => setPregnancyData(prev => ({
    ...prev, dg60: { ...prev.dg60, checks: moverLinha(prev.dg60.checks, de, para) },
  })));

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!isInitialized || !service?.id) return;

      // A TE acabou de ser gravada no mesmo "Salvar" (o modal grava em
      // ordem): a DG sai acertada com ela, com o id que a transferência
      // acabou de ganhar. Sem isso, a linha nova ia sem vínculo.
      let dados = pregnancyData;
      try {
        const r = await acertarComATE(pregnancyData);
        if (r) { dados = r.dados; setPregnancyData(r.dados); }
      } catch (err) {
        console.warn('[dg] gravando sem conferir com a TE:', err.message);
      }

      // Nada em que alguém tenha tocado não vira registro no banco. A regra
      // mora em `dgTemConteudo`, com teste, e vale por sessão: era aqui um
      // "as duas vazias" que deixava passar a DG60 em branco junto da DG30
      // preenchida — e gravar a DG60 vazia APAGA as checagens que ela tivesse,
      // porque a gravação troca a lista inteira.
      if (!dgTemConteudo(dados.dg30) && !dgTemConteudo(dados.dg60)) {
        return undefined;
      }

      // Cada DG cobra o seu técnico ao CONCLUIR, não ao lançar conferência.
      // Exigir durante o preenchimento travava quem ainda estava registrando
      // os resultados — e em campo se salva várias vezes antes de fechar.
      [['dg30', 'DG30'], ['dg60', 'DG60']].forEach(([chave, rotulo]) => {
        const etapa = dados[chave];
        if (!etapa.completed) return;
        if (!etapa.session.technician_id && !etapa.session.technician_name) {
          throw new Error(`Informe o técnico responsável pela ${rotulo} antes de concluir a etapa.`);
        }
      });
      const billingQty = calculateBillingQuantity();
      const snapshot = { ...dados, dg60: { ...dados.dg60, billing_quantity: billingQty } };
      return saveDGData(service.id, snapshot);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isInitialized, pregnancyData, service?.id]);

  const dg30Stats = {
    total: pregnancyData.dg30.checks.length,
    pregnant: pregnancyData.dg30.checks.filter(c => c.result === DG30_RESULTS.PREGNANT).length,
    empty: pregnancyData.dg30.checks.filter(c => c.result === DG30_RESULTS.EMPTY).length,
    inconclusive: pregnancyData.dg30.checks.filter(c => c.result === DG30_RESULTS.INCONCLUSIVE).length,
    pending: pregnancyData.dg30.checks.filter(c => !c.result).length
  };

  const dg60Stats = {
    total: pregnancyData.dg60.checks.length,
    pregnant: pregnancyData.dg60.checks.filter(c => c.result === DG60_RESULTS.PREGNANT).length,
    aborted: pregnancyData.dg60.checks.filter(c => c.result === DG60_RESULTS.ABORTED).length,
    pending: pregnancyData.dg60.checks.filter(c => !c.result).length
  };

  return (
    <div className="p-4">
      {/* Sub-Tabs */}
      <div className="mb-4 border-b">
        <nav className="flex gap-2 items-center">
          <button
            onClick={() => setActiveSubTab('dg30')}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeSubTab === 'dg30' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <Calendar className="w-4 h-4" />
            DG 30 Dias
            {dg30Stats.total > 0 && (
              <span className="ml-1 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">{dg30Stats.total}</span>
            )}
          </button>
          <button
            onClick={() => setActiveSubTab('dg60')}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeSubTab === 'dg60' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <DollarSign className="w-4 h-4" />
            DG 60 Dias (Faturamento)
            {dg60Stats.total > 0 && (
              <span className="ml-1 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">{dg60Stats.total}</span>
            )}
          </button>
        </nav>
      </div>

      {/* DG 30D */}
      {activeSubTab === 'dg30' && (
        <div>
          {/* Cabecalho da Sessao */}
          <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-slate-900 text-sm">Sessao DG 30 Dias</h4>
              <div className="flex items-center gap-2">
                <ImportarDaEtapa
                  rotulo="Importar da TE"
                  fonte="da TE"
                  singular="receptora trazida"
                  plural="receptoras trazidas"
                  onImportar={importarDaTE}
                  onSubstituir={substituirPelaTE}
                  rotuloSubstituir="Substituir pela TE"
                />
                {dg30Stats.total > 0 && (
                  <button
                    onClick={() => generateDG30PDF(service, pregnancyData.dg30)}
                    className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded transition-colors"
                  >
                    <FileDown className="w-3.5 h-3.5" /> PDF
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Data *</label>
                <input
                  type="date"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={pregnancyData.dg30.session.date || ''}
                  onChange={(e) => handleDG30SessionChange('date', e.target.value)}
                />
              </div>
              <EquipeDaEtapa
                laboratoryId={laboratoryId}
                responsavel={(pregnancyData.dg30.session.technician_id || pregnancyData.dg30.session.technician_name
              ? { id: pregnancyData.dg30.session.technician_id || null, nome: pregnancyData.dg30.session.technician_name }
              : null)}
                planejado={isInitialized ? equipe30.planejado : null}
                ajudante={equipe30.ajudante}
                onResponsavel={(id, nome) => {
                  handleDG30SessionChange('technician_id', id);
                  handleDG30SessionChange('technician_name', nome);
                  equipe30.responsavelNaEquipe(id, nome);
                }}
                onAjudante={equipe30.ajudanteNaEquipe}
              />
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Metodo</label>
                <select
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={pregnancyData.dg30.session.method || ''}
                  onChange={(e) => handleDG30SessionChange('method', e.target.value)}
                >
                  {Object.values(DIAGNOSTIC_METHODS).map(method => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {avisoDaTE && (
            <p className="mb-4 px-3 py-2 text-xs text-slate-700 bg-blue-50 border border-blue-100 rounded-lg">
              {avisoDaTE} Nada foi gravado ainda: vale ao clicar em "Salvar Workflow".
            </p>
          )}

          {/* Estatisticas */}
          <div className="mb-4 p-3 bg-white border border-slate-200 rounded-lg">
            <div className="grid grid-cols-5 gap-3 text-xs">
              <div><p className="text-slate-500">Total</p><p className="font-semibold text-slate-900 text-sm">{dg30Stats.total}</p></div>
              <div><p className="text-slate-500">{MARK.OK} Prenhas</p><p className="font-semibold text-slate-900 text-sm">{dg30Stats.pregnant}</p></div>
              <div><p className="text-slate-500">{MARK.BAD} Vazias</p><p className="font-semibold text-slate-900 text-sm">{dg30Stats.empty}</p></div>
              <div><p className="text-slate-500">{MARK.UNKNOWN} Inconcl.</p><p className="font-semibold text-slate-900 text-sm">{dg30Stats.inconclusive}</p></div>
              <div><p className="text-slate-500">{MARK.PENDING} Pendentes</p><p className="font-semibold text-slate-500 text-sm">{dg30Stats.pending}</p></div>
            </div>
          </div>

          {/* Tabela DG30 */}
          {pregnancyData.dg30.checks.length === 0 ? (
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center text-slate-500 mb-4">
              <p className="text-sm">Nenhuma receptora para diagnostico.</p>
              <p className="text-xs mt-1">As receptoras vem da aba TE. Se ja ha transferencia aprovada la, use "Importar da TE".</p>
            </div>
          ) : (
            <div className="overflow-x-auto sm:overflow-visible sm:w-max sm:min-w-full rounded-lg border border-slate-200 mb-4">
              <table className="w-full text-xs border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Receptora</th>
                    <th className="px-2 py-2 text-center font-semibold text-slate-600 whitespace-nowrap">Embriao</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">DG Previsto</th>
                    <th className="px-2 py-2 text-center font-semibold text-slate-600 whitespace-nowrap">Resultado</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Parto previsto</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Viabilidade</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Batimentos</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Tamanho (mm)</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Causa / Motivo</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Reagendar</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Obs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {pregnancyData.dg30.checks.map((check, index) => {
                    const { realce, ...linhaArrastavel } = arrastarDG30.linha(index);
                    return (
                    <tr
                      key={check.id}
                      {...linhaArrastavel}
                      className={`hover:bg-slate-50 transition-colors ${realce}`}
                    >
                      {/* Receptora, com a alça de arrastar */}
                      <td className="px-2 py-2 font-medium text-slate-800 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <AlcaDeArrastar props={arrastarDG30.alca(index, pregnancyData.dg30.checks.length)} rotulo="receptora" />
                          {check.recipient_identifier || '—'}
                          {check._foraDaTE && (
                            <>
                              <span className="text-xs font-normal text-slate-500" title="A transferência desta receptora saiu da TE">
                                {MARK.WARN} fora da TE
                              </span>
                              <button onClick={() => removerForaDaTE('dg30', index)}
                                title="Tirar do diagnóstico" className="text-slate-300 hover:text-red-600">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </span>
                      </td>

                      {/* Embriao # */}
                      <td className="px-2 py-2 text-center">
                        <span className="text-blue-700 font-semibold">#{check.embryo_number ?? '—'}</span>
                      </td>

                      {/* DG Previsto */}
                      <td className="px-2 py-2 whitespace-nowrap text-slate-600">
                        {fmt(check.expected_date && String(check.expected_date).slice(0, 10))}
                      </td>

                      {/* Resultado - botoes compactos */}
                      <td className="px-2 py-2">
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleDG30CheckChange(index, 'result', DG30_RESULTS.PREGNANT)}
                            className={`px-2 py-1 rounded text-xs font-bold border transition-colors ${
                              check.result === DG30_RESULTS.PREGNANT
                                ? 'bg-slate-800 text-white border-slate-800'
                                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            {MARK.OK} PRENHE
                          </button>
                          <button
                            onClick={() => handleDG30CheckChange(index, 'result', DG30_RESULTS.EMPTY)}
                            className={`px-2 py-1 rounded text-xs font-bold border transition-colors ${
                              check.result === DG30_RESULTS.EMPTY
                                ? 'bg-slate-800 text-white border-slate-800'
                                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            {MARK.BAD} VAZIA
                          </button>
                          <button
                            onClick={() => handleDG30CheckChange(index, 'result', DG30_RESULTS.INCONCLUSIVE)}
                            className={`px-2 py-1 rounded text-xs font-bold border transition-colors ${
                              check.result === DG30_RESULTS.INCONCLUSIVE
                                ? 'bg-slate-800 text-white border-slate-800'
                                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            {MARK.UNKNOWN} INCONCL.
                          </button>
                        </div>
                      </td>

                      {/* Parto previsto (so se prenhe) */}
                      <td className="px-2 py-2 whitespace-nowrap text-slate-600">
                        {check.result === DG30_RESULTS.PREGNANT ? fmt(previsaoDeParto(check.te_date)) : <span className="text-slate-300">—</span>}
                      </td>

                      {/* Viabilidade (so se prenhe) */}
                      <td className="px-2 py-2" style={{ minWidth: '100px' }}>
                        {check.result === DG30_RESULTS.PREGNANT ? (
                          <select
                            className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={check.viability || ''}
                            onChange={(e) => handleDG30CheckChange(index, 'viability', e.target.value)}
                          >
                            <option value="">-</option>
                            <option value="Viavel">Viavel</option>
                            <option value="Duvidoso">Duvidoso</option>
                          </select>
                        ) : <span className="text-slate-300 px-1">—</span>}
                      </td>

                      {/* Batimentos (so se prenhe) */}
                      <td className="px-2 py-2" style={{ minWidth: '100px' }}>
                        {check.result === DG30_RESULTS.PREGNANT ? (
                          <select
                            className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={check.heartbeat || ''}
                            onChange={(e) => handleDG30CheckChange(index, 'heartbeat', e.target.value)}
                          >
                            <option value="">-</option>
                            <option value="Presentes">Presentes</option>
                            <option value="Ausentes">Ausentes</option>
                          </select>
                        ) : <span className="text-slate-300 px-1">—</span>}
                      </td>

                      {/* Tamanho */}
                      <td className="px-2 py-2" style={{ minWidth: '80px' }}>
                        {check.result === DG30_RESULTS.PREGNANT ? (
                          <input
                            type="number" min="0" step="0.1" placeholder="Ex: 12"
                            className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={check.size_mm || ''}
                            onChange={(e) => handleDG30CheckChange(index, 'size_mm', e.target.value)}
                          />
                        ) : <span className="text-slate-300 px-1">—</span>}
                      </td>

                      {/* Causa / Motivo inconclusivo */}
                      <td className="px-2 py-2" style={{ minWidth: '130px' }}>
                        {check.result === DG30_RESULTS.EMPTY ? (
                          <select
                            className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={check.empty_cause || ''}
                            onChange={(e) => handleDG30CheckChange(index, 'empty_cause', e.target.value)}
                          >
                            <option value="">-</option>
                            {Object.values(EMPTY_CAUSES).map(cause => (
                              <option key={cause} value={cause}>{cause}</option>
                            ))}
                          </select>
                        ) : check.result === DG30_RESULTS.INCONCLUSIVE ? (
                          <select
                            className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={check.inconclusive_reason || ''}
                            onChange={(e) => handleDG30CheckChange(index, 'inconclusive_reason', e.target.value)}
                          >
                            <option value="">-</option>
                            {Object.values(INCONCLUSIVE_REASONS).map(reason => (
                              <option key={reason} value={reason}>{reason}</option>
                            ))}
                          </select>
                        ) : <span className="text-slate-300 px-1">—</span>}
                      </td>

                      {/* Reagendar (inconclusivo) */}
                      <td className="px-2 py-2" style={{ minWidth: '110px' }}>
                        {check.result === DG30_RESULTS.INCONCLUSIVE ? (
                          <input
                            type="date"
                            className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={check.reschedule_date || ''}
                            onChange={(e) => handleDG30CheckChange(index, 'reschedule_date', e.target.value)}
                          />
                        ) : <span className="text-slate-300 px-1">—</span>}
                      </td>

                      {/* Observacoes */}
                      <td className="px-2 py-2" style={{ minWidth: '120px' }}>
                        <input
                          type="text" placeholder="Obs..."
                          className="w-full px-2 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          value={check.observations || ''}
                          onChange={(e) => handleDG30CheckChange(index, 'observations', e.target.value)}
                        />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Campo de quantidade manual */}
          {pregnancyData.dg30.checks.length === 0 && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Quantidade de Prenhezes 30D (Manual)
              </label>
              <p className="text-xs text-slate-500 mb-3">
                Este servico nao possui checks detalhados. Preencha manualmente.
              </p>
              <input
                type="number" min="0" step="1" placeholder="Ex: 5"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={manualQuantity30D || ''}
                onChange={(e) => setManualQuantity30D(parseInt(e.target.value) || 0)}
              />
            </div>
          )}

          {pregnancyData.dg30.checks.length > 0 && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg mb-4">
              <p className="text-sm text-slate-700">
                <strong>{dg30Stats.pregnant} prenhas</strong> de {dg30Stats.total} — <strong>{dg30Stats.empty} vazias</strong>, <strong>{dg30Stats.inconclusive} inconclusivos</strong>
              </p>
            </div>
          )}

          {/* Checkbox Concluido */}
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded">
            <input
              type="checkbox"
              id="dg30_completed"
              checked={pregnancyData.dg30.completed || false}
              onChange={(e) => setPregnancyData(prev => ({
                ...prev,
                dg30: { ...prev.dg30, completed: e.target.checked }
              }))}
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            <label htmlFor="dg30_completed" className="text-sm font-medium text-slate-700">
              Marcar DG 30D como concluido
            </label>
          </div>
        </div>
      )}

      {/* DG 60D */}
      {activeSubTab === 'dg60' && (
        <div>
          {/* Cabecalho da Sessao */}
          <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-slate-900 text-sm">Sessao DG 60 Dias (Faturamento)</h4>
              {dg60Stats.total > 0 && (
                <button
                  onClick={() => generateDG60PDF(service, pregnancyData.dg60)}
                  className="flex items-center gap-1 text-xs text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 px-2 py-1 rounded transition-colors"
                >
                  <FileDown className="w-3.5 h-3.5" /> PDF
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Data *</label>
                <input
                  type="date"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={pregnancyData.dg60.session.date || ''}
                  onChange={(e) => handleDG60SessionChange('date', e.target.value)}
                />
              </div>
              <EquipeDaEtapa
                laboratoryId={laboratoryId}
                responsavel={(pregnancyData.dg60.session.technician_id || pregnancyData.dg60.session.technician_name
              ? { id: pregnancyData.dg60.session.technician_id || null, nome: pregnancyData.dg60.session.technician_name }
              : null)}
                planejado={isInitialized ? equipe60.planejado : null}
                ajudante={equipe60.ajudante}
                onResponsavel={(id, nome) => {
                  handleDG60SessionChange('technician_id', id);
                  handleDG60SessionChange('technician_name', nome);
                  equipe60.responsavelNaEquipe(id, nome);
                }}
                onAjudante={equipe60.ajudanteNaEquipe}
              />
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Metodo</label>
                <select
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={pregnancyData.dg60.session.method || ''}
                  onChange={(e) => handleDG60SessionChange('method', e.target.value)}
                >
                  {Object.values(DIAGNOSTIC_METHODS).map(method => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
            </div>

            {pregnancyData.dg60.checks.length === 0 && dg30Stats.pregnant > 0 && pregnancyData.dg30.completed && (
              <div className="mt-3">
                <button
                  onClick={handleImportFromDG30}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  Importar {dg30Stats.pregnant} prenha(s) confirmadas do DG 30D
                </button>
                <p className="text-xs text-slate-500 mt-1 text-center">
                  As receptoras prenhas serao automaticamente importadas quando voce completar o DG 30D
                </p>
              </div>
            )}
          </div>

          {/* Estatisticas + Faturamento */}
          <div className="mb-4 p-3 bg-white border border-slate-200 rounded-lg">
            <div className="grid grid-cols-3 gap-3 text-xs mb-3">
              <div><p className="text-slate-500">Total</p><p className="font-semibold text-slate-900 text-sm">{dg60Stats.total}</p></div>
              <div><p className="text-slate-500">{MARK.OK} Prenhas</p><p className="font-semibold text-slate-900 text-sm">{dg60Stats.pregnant}</p></div>
              <div><p className="text-slate-500">{MARK.BAD} Abortadas</p><p className="font-semibold text-slate-900 text-sm">{dg60Stats.aborted}</p></div>
            </div>
            {service?.billing_model === BILLING_MODELS.POR_PRENHEZ && dg60Stats.pregnant > 0 && (
              <div className="p-2 bg-slate-50 rounded border border-slate-200">
                <p className="text-sm font-bold text-slate-900">
                  Valor a Faturar: R$ {(dg60Stats.pregnant * (parseFloat(service.value_per_pregnancy) || 1200)).toLocaleString('pt-BR')}
                </p>
                <p className="text-xs text-slate-500">
                  {dg60Stats.pregnant} prenhezes x R$ {(parseFloat(service.value_per_pregnancy) || 1200).toFixed(2)}
                </p>
              </div>
            )}
          </div>

          {/* Tabela DG60 */}
          {pregnancyData.dg60.checks.length === 0 ? (
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center text-slate-500 mb-4">
              <p className="text-sm">Nenhuma prenhez para conferencia.</p>
              <p className="text-xs mt-1">Use o botao "Importar do DG 30D" para carregar as prenhas.</p>
            </div>
          ) : (
            <div className="overflow-x-auto sm:overflow-visible sm:w-max sm:min-w-full rounded-lg border border-slate-200 mb-4">
              <table className="w-full text-xs border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Receptora</th>
                    <th className="px-2 py-2 text-center font-semibold text-slate-600 whitespace-nowrap">Embriao</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">DG 30D</th>
                    <th className="px-2 py-2 text-center font-semibold text-slate-600 whitespace-nowrap">Resultado</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Parto previsto</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Viabilidade</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Batimentos</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Tamanho (mm)</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Sexagem</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Sexo</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Confianca</th>
                    <th className="px-2 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Obs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {pregnancyData.dg60.checks.map((check, index) => {
                    const { realce, ...linhaArrastavel } = arrastarDG60.linha(index);
                    return (
                    <tr
                      key={check.id}
                      {...linhaArrastavel}
                      className={`hover:bg-slate-50 transition-colors ${realce}`}
                    >
                      {/* Receptora, com a alça de arrastar */}
                      <td className="px-2 py-2 font-medium text-slate-800 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <AlcaDeArrastar props={arrastarDG60.alca(index, pregnancyData.dg60.checks.length)} rotulo="receptora" />
                          {check.recipient_identifier || '—'}
                          {check._foraDaTE && (
                            <>
                              <span className="text-xs font-normal text-slate-500" title="A transferência desta receptora saiu da TE">
                                {MARK.WARN} fora da TE
                              </span>
                              <button onClick={() => removerForaDaTE('dg60', index)}
                                title="Tirar do diagnóstico" className="text-slate-300 hover:text-red-600">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </span>
                      </td>

                      {/* Embriao */}
                      <td className="px-2 py-2 text-center">
                        <span className="text-blue-700 font-semibold">#{check.embryo_number ?? '—'}</span>
                      </td>

                      {/* DG 30D data */}
                      <td className="px-2 py-2 whitespace-nowrap text-slate-600">
                        {fmt(check.dg30_date && String(check.dg30_date).slice(0, 10))}
                      </td>

                      {/* Resultado */}
                      <td className="px-2 py-2">
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleDG60CheckChange(index, 'result', DG60_RESULTS.PREGNANT)}
                            className={`px-2 py-1 rounded text-xs font-bold border transition-colors ${
                              check.result === DG60_RESULTS.PREGNANT
                                ? 'bg-slate-800 text-white border-slate-800'
                                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            {MARK.OK} PRENHE
                          </button>
                          <button
                            onClick={() => handleDG60CheckChange(index, 'result', DG60_RESULTS.ABORTED)}
                            className={`px-2 py-1 rounded text-xs font-bold border transition-colors ${
                              check.result === DG60_RESULTS.ABORTED
                                ? 'bg-slate-800 text-white border-slate-800'
                                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            {MARK.BAD} ABORTOU
                          </button>
                        </div>
                      </td>

                      {/* Parto previsto (so se prenhe) */}
                      <td className="px-2 py-2 whitespace-nowrap text-slate-600">
                        {check.result === DG60_RESULTS.PREGNANT ? fmt(previsaoDeParto(check.te_date)) : <span className="text-slate-300">—</span>}
                      </td>

                      {/* Viabilidade */}
                      <td className="px-2 py-2" style={{ minWidth: '110px' }}>
                        {check.result === DG60_RESULTS.PREGNANT ? (
                          <select
                            className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={check.viability || ''}
                            onChange={(e) => handleDG60CheckChange(index, 'viability', e.target.value)}
                          >
                            <option value="">-</option>
                            <option value="Viavel">Viavel</option>
                            <option value="Comprometida">Comprometida</option>
                          </select>
                        ) : <span className="text-slate-300 px-1">—</span>}
                      </td>

                      {/* Batimentos */}
                      <td className="px-2 py-2" style={{ minWidth: '100px' }}>
                        {check.result === DG60_RESULTS.PREGNANT ? (
                          <select
                            className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={check.heartbeat || ''}
                            onChange={(e) => handleDG60CheckChange(index, 'heartbeat', e.target.value)}
                          >
                            <option value="">-</option>
                            {Object.values(HEARTBEAT_STATUS).map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        ) : <span className="text-slate-300 px-1">—</span>}
                      </td>

                      {/* Tamanho */}
                      <td className="px-2 py-2" style={{ minWidth: '80px' }}>
                        {check.result === DG60_RESULTS.PREGNANT ? (
                          <input
                            type="number" min="0" step="0.1" placeholder="Ex: 8.5"
                            className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={check.size_mm || ''}
                            onChange={(e) => handleDG60CheckChange(index, 'size_mm', e.target.value)}
                          />
                        ) : <span className="text-slate-300 px-1">—</span>}
                      </td>

                      {/* Sexagem (checkbox) */}
                      <td className="px-2 py-2 text-center">
                        {check.result === DG60_RESULTS.PREGNANT ? (
                          <input
                            type="checkbox"
                            checked={check.sexing?.performed || false}
                            onChange={(e) => handleDG60CheckChange(index, 'sexing.performed', e.target.checked)}
                            className="form-checkbox h-4 w-4 text-blue-600"
                            title="Realizar sexagem fetal"
                          />
                        ) : <span className="text-slate-300">—</span>}
                      </td>

                      {/* Sexo */}
                      <td className="px-2 py-2" style={{ minWidth: '90px' }}>
                        {check.result === DG60_RESULTS.PREGNANT && check.sexing?.performed ? (
                          <select
                            className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={check.sexing?.sex || ''}
                            onChange={(e) => handleDG60CheckChange(index, 'sexing.sex', e.target.value)}
                          >
                            <option value="">-</option>
                            {Object.values(FETUS_SEX).map(sex => (
                              <option key={sex} value={sex}>{sex}</option>
                            ))}
                          </select>
                        ) : <span className="text-slate-300 px-1">—</span>}
                      </td>

                      {/* Confianca */}
                      <td className="px-2 py-2" style={{ minWidth: '90px' }}>
                        {check.result === DG60_RESULTS.PREGNANT && check.sexing?.performed ? (
                          <select
                            className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            value={check.sexing?.confidence || ''}
                            onChange={(e) => handleDG60CheckChange(index, 'sexing.confidence', e.target.value)}
                          >
                            <option value="">-</option>
                            {Object.values(SEXING_CONFIDENCE).map(conf => (
                              <option key={conf} value={conf}>{conf}</option>
                            ))}
                          </select>
                        ) : <span className="text-slate-300 px-1">—</span>}
                      </td>

                      {/* Observacoes */}
                      <td className="px-2 py-2" style={{ minWidth: '120px' }}>
                        <input
                          type="text" placeholder="Obs..."
                          className="w-full px-2 py-0.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                          value={check.observations || ''}
                          onChange={(e) => handleDG60CheckChange(index, 'observations', e.target.value)}
                        />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Campo de quantidade manual */}
          {pregnancyData.dg60.checks.length === 0 && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Quantidade de Prenhezes 60D Confirmadas (Manual)
              </label>
              <p className="text-xs text-slate-500 mb-3">
                Este servico nao possui checks detalhados. Preencha manualmente.
              </p>
              <input
                type="number" min="0" step="1" placeholder="Ex: 4"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={manualQuantity60D || ''}
                onChange={(e) => setManualQuantity60D(parseInt(e.target.value) || 0)}
              />
            </div>
          )}

          {pregnancyData.dg60.checks.length > 0 && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg mb-4">
              <p className="text-sm text-slate-700">
                <strong>{dg60Stats.pregnant} prenhezes confirmadas</strong> de {dg60Stats.total} ({dg60Stats.aborted} abortadas, {dg60Stats.pending} pendentes)
              </p>
            </div>
          )}

          {/* Checkbox Concluido */}
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded">
            <input
              type="checkbox"
              id="dg60_completed"
              checked={pregnancyData.dg60.completed || false}
              onChange={(e) => setPregnancyData(prev => ({
                ...prev,
                dg60: { ...prev.dg60, completed: e.target.checked }
              }))}
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            <label htmlFor="dg60_completed" className="text-sm font-medium text-slate-700">
              Marcar DG 60D como concluido
            </label>
            <span className="ml-auto text-xs text-slate-500">Esta e a conferencia que define o faturamento</span>
          </div>
        </div>
      )}
    </div>
  );
});

export default PregnancyTab;
